"use client"

import { useState } from "react"
import { useFormatter, useTranslations } from "next-intl"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { usePermissions } from "@/hooks/use-permissions"
import { setFlightCheckIn } from "@/lib/logistics-api"
import { cn } from "@/lib/utils"

import type { UnifiedRow } from "./use-needs-booking"

/**
 * Radiance 261001 — the logistics desk's Yes / No for the patient's flight
 * check-in, where the desk's Book button would be: this desk books no visits,
 * it checks patients in. Online check-in opens 24 hours before the flight;
 * the hover says when, and the `flight_checkin_due` bell reminds them.
 *
 * A row stays on the board until its driver AND its check-in are done, so a
 * Yes on a row whose driver is set takes it off the pending view — onChanged
 * re-reads the board for exactly that.
 */
export function FlightCheckInToggle({ row, onChanged }: { row: UnifiedRow; onChanged: () => void }) {
  const t = useTranslations("dashboard.frontdeskBooking.checkIn")
  const format = useFormatter()
  const { can } = usePermissions()
  const [saving, setSaving] = useState(false)
  // Shown at once; the board re-read then confirms it (or the row leaves).
  const [checkedIn, setCheckedIn] = useState(row.checkIn?.checkedIn ?? false)

  const checkIn = row.checkIn
  if (!checkIn) return null

  const opens = checkIn.opensAt
    ? t("opens", { when: format.dateTime(checkIn.opensAt, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) })
    : undefined
  const canSet = can("logistics.manage")

  const choose = async (next: boolean) => {
    if (next === checkedIn || saving) return
    setSaving(true)
    setCheckedIn(next)
    try {
      await setFlightCheckIn(checkIn.travelId, checkIn.leg, next)
      onChanged()
    } catch {
      setCheckedIn(!next)
      toast.error(t("failed"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <span
      role="group"
      aria-label={t(checkIn.leg === "arrival" ? "ariaArrival" : "ariaDeparture", { name: row.patientName })}
      title={opens}
      className="inline-flex overflow-hidden rounded-md border border-border"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {[true, false].map((value) => {
        const selected = checkedIn === value
        return (
          <Button
            key={String(value)}
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canSet || saving}
            aria-pressed={selected}
            onClick={() => choose(value)}
            className={cn(
              "h-6 rounded-none px-2 text-[11px]",
              selected && value && "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-400",
              selected && !value && "bg-surface-2 text-foreground",
              !selected && "text-text-dim",
            )}
          >
            {value ? t("yes") : t("no")}
          </Button>
        )
      })}
    </span>
  )
}
